import React, { useEffect, useMemo, useState } from 'react';
import {
  ListFilter,
  UsersRound,
  ArrowDownNarrowWide,
  User as UserIcon,
  Scale,
  HandCoins,
  ChevronDown,
  Check,
  CircleDotDashed,
  BanknoteArrowUp,
  ChevronLeft,
  Database,
  ArrowUpNarrowWide,
  ArrowUp,
  ArrowDown,
  Wallet,
  UserRoundPen,
  UserSquare2,
} from 'lucide-react';
import Pagination from '../../../components/ui/pagination/Pagination';
import SearchInput from '../../../components/ui/inputs/SearchInput';
import ClientAccountBadge from '../../../components/ui/badges/ClientAccountBadge';
import ClientBalancingNoteBadge from '../../../components/ui/badges/ClientBalancingNoteBadge';
import CustomButton from '../../../components/ui/buttons/CustomButton';
import CustomCheckbox from '../../../components/ui/checkboxes/CustomCheckbox';
import CustomInput from '../../../components/ui/inputs/CustomInput';
import Button from '../../../components/ui/buttons/Button';
import DashboardTab from '../../planner/client/dashboard/DashboardTab';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import EditAssetModal from '../../../components/modals/assets/EditAssetModal';
import AssetStatusBadge from '../../../components/ui/badges/AssetStatusBadge';
import { formatCNPJ } from '../../../utils/masks';
import ManageBasesModal from '../../../components/modals/assets/ManageBasesModal';
import AddAssetModal from '../../../components/modals/assets/AddAssetModal';
import SortableHeader from '../../../components/ui/table/SortableHeader';
import Select from '../../../components/ui/selects/Select';
import { cn } from '../../../utils/classnames';
import { useToast } from '../../../components/ui/toast/ToastContext';
import Input from '../../../components/ui/inputs/Input';
import CustomSelect from '../../../components/ui/selects/CustomSelect';
import ColorSelect from '../../../components/ui/selects/ColorSelect';
import { snakecaseKeys } from '../../../utils/snakecaseKeys';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';

const WalletProfiles = () => {
  const [walletProfiles, setWalletProfiles] = useState([]);
  const [selectedWalletProfileId, setSelectedWalletProfileId] = useState(null);
  const [currentWalletProfile, setCurrentWalletProfile] = useState(null);
  const [currentWalletProfileForm, setCurrentWalletProfileForm] = useState({
    name: '',
    riskProfile: '',
    colorHex: '#000000',
    grade: '',
    walletsAttributes: [],
  });
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();
  const [riskProfileOptions, setRiskProfileOptions] = useState([]);

  const fetchData = () => {
    setLoading(true);
    fetch('/app/analyst/wallet_configs/wallet_profiles')
      .then((res) => res.json())
      .then((data) => {
        setWalletProfiles(camelizeKeys(data).walletProfiles);
        setSelectedWalletProfileId(camelizeKeys(data).walletProfiles[0]?.id || null);
        setRiskProfileOptions(camelizeKeys(data).riskProfileOptions || []);
      })
      .catch((err) => {
        console.error('Erro ao carregar dados', err);
        addToast({ type: 'error', message: 'Erro ao carregar dados' });
      })
      .finally(() => setLoading(false));
  };

  const fetchWalletProfileDetails = (walletId) => {
    setLoading(true);
    fetch(`/app/analyst/wallet_configs/wallet_profiles/${walletId}`)
      .then((res) => res.json())
      .then((data) => {
        const walletProfile = camelizeKeys(data).walletProfile;
        setCurrentWalletProfile(walletProfile);
        setCurrentWalletProfileForm({
          name: walletProfile.name,
          riskProfile: walletProfile.riskProfile,
          colorHex: walletProfile.colorHex,
          grade: walletProfile.grade,
          walletsAttributes: walletProfile.walletsAttributes,
        });
        console.log('Detalhes do perfil de carteira:', walletProfile);
      })
      .catch((err) => addToast({ type: 'error', message: 'Erro ao carregar detalhes' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedWalletProfileId) {
      fetchWalletProfileDetails(selectedWalletProfileId);
    } else {
      setCurrentWalletProfile(null);
    }
  }, [selectedWalletProfileId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentWalletProfileForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggleEnabled = (walletId) => {
    setCurrentWalletProfileForm((prev) => {
      const updatedAttributes = prev.walletsAttributes.map((attr) => {
        if (attr.id === walletId) {
          return { ...attr, enabled: !attr.enabled };
        }
        return attr;
      });
      return { ...prev, walletsAttributes: updatedAttributes };
    });
  };

  const handleDiscard = () => {
    if (selectedWalletProfileId) {
      fetchWalletProfileDetails(selectedWalletProfileId);
      addToast({ type: 'info', message: 'Alterações descartadas.' });
    }
  };

  const handleSave = async () => {
    setLoading(true);

    const payload = snakecaseKeys({ walletProfile: currentWalletProfileForm });

    try {
      const res = await fetchWithCsrf(`/app/analyst/wallet_configs/wallet_profiles/${selectedWalletProfileId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      });

      if (!res.ok) throw new Error('Falha ao salvar');

      addToast({ type: 'success', message: 'Perfil atualizado com sucesso!' });

      fetchData();
      fetchWalletProfileDetails(selectedWalletProfileId);
    } catch (error) {
      addToast({ type: 'error', message: error.message || 'Erro ao salvar alterações.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-0 flex flex-col h-full flex-1 min-h-0">
      <div className="grid gap-5 h-full" style={{ gridTemplateColumns: '400px minmax(0, 1fr)' }}>
        {/* Coluna 1 */}
        <div className="bg-white p-7 rounded-custom20 gap-5 flex flex-col h-fit">
          <span className="flex items-center gap-2">
            <UserSquare2 className="text-tealMedium" size={20} strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold text-blueDarker">Perfis de Carteira</h1>
          </span>
          <div className="space-y-4 overflow-y-auto custom-scrollbar pr-2 flex-1 min-h-0">
            {walletProfiles.map((profile) => (
              <div
                key={profile.id}
                className={cn(
                  'flex flex-row gap-2 items-center p-4 bg-blueLighter rounded-lg',
                  selectedWalletProfileId === profile.id ? 'bg-blueLight' : 'bg-transparent hover:bg-blueUltraLight',
                  'cursor-pointer'
                )}
                onClick={() => setSelectedWalletProfileId(profile.id)}
                style={{ borderRadius: '10px' }}
              >
                <span
                  className="flex-none block rounded"
                  style={{ backgroundColor: profile.colorHex, width: '12px', height: '12px' }}
                />
                <h2 className="text-lg font-semibold text-blueDarker">{profile.name}</h2>
              </div>
            ))}
          </div>
        </div>

        {/* Coluna 2*/}
        <div className="bg-white p-7 rounded-custom20 gap-5 flex flex-col h-full min-h-0">
          <div className="flex items-center gap-2">
            <UserRoundPen className="text-tealMedium" size={20} strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold text-blueDarker">Editar Carteira de Investimento</h1>
            <div className="bg-blueLight px-3 py-1 text-xl text-blueDarker font-normal flex items-center gap-3 rounded-lg">
              <span
                className="flex-none block rounded"
                style={{ backgroundColor: currentWalletProfile?.colorHex, width: '12px', height: '12px' }}
              />
              <span className="font-normal">{currentWalletProfile?.name}</span>
              <span
                className="bg-white text-sm text-center"
                style={{ width: '20px', height: '20px', borderRadius: '4px' }}
              >
                {currentWalletProfile?.grade}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-5 w-full items-start pr-2">
            <div className="col-span-6">
              <Input
                name="name"
                label="Nome do Perfil"
                value={currentWalletProfileForm.name || ''}
                onChange={handleChange}
              />
            </div>

            <div className="col-span-3">
              <ColorSelect
                name="colorHex"
                label="Cor da Legenda"
                value={currentWalletProfileForm.colorHex || ''}
                onChange={handleChange}
                withText={true}
              />
            </div>

            <div className="col-span-3">
              <CustomSelect
                name="riskProfile"
                label="Nível de Risco"
                options={riskProfileOptions.map((option) => ({ value: option.riskProfile, label: option.riskLabel }))}
                value={currentWalletProfileForm.riskProfile || ''}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 items-center mt-2">
            <div className="flex gap-2 items-center">
              <Wallet size={20} className="text-tealMedium" strokeWidth={1.5} />
              <span className="text-xl font-semibold text-blueDarker">Carteiras de Investimento habilitadas:</span>
            </div>
            <div className="flex gap-2 items-center">
              <Wallet size={20} className="text-tealMedium" strokeWidth={1.5} />
              <span className="text-xl font-semibold text-blueDarker">Carteiras de Previdência habilitadas:</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 flex-1 min-h-0">
            <div className="pr-2 custom-scrollbar overflow-y-auto">
              {currentWalletProfileForm?.walletsAttributes
                .filter((wa) => wa.walletType === 'investment')
                .map((wa, idx) => {
                  const isBlueRow = idx % 2 !== 1;
                  return (
                    <div
                      key={wa.id}
                      className={`px-5 py-5 text-xl text-blueDarker font-normal flex items-center gap-3 ${
                        isBlueRow ? 'bg-blueLight' : 'bg-transparent'
                      }`}
                      style={{ borderRadius: '12px' }}
                    >
                      <CustomCheckbox checked={wa.enabled} onChange={() => handleToggleEnabled(wa.id)} />
                      <span className="font-semibold text-blueDarker text-base">{wa.name}</span>
                    </div>
                  );
                })}
              {currentWalletProfileForm.walletsAttributes.filter((wa) => wa.walletType === 'investment').length ===
                0 && (
                <p className="text-sm text-gray-400 p-4 text-center">Nenhuma carteira de investimento disponível.</p>
              )}
            </div>
            <div className="pr-2 custom-scrollbar overflow-y-auto">
              {currentWalletProfileForm?.walletsAttributes
                .filter((wa) => wa.walletType === 'pension')
                .map((wa, idx) => {
                  const isBlueRow = idx % 2 !== 1;
                  return (
                    <div
                      key={wa.id}
                      className={`px-5 py-5 text-xl text-blueDarker font-normal flex items-center gap-3 ${
                        isBlueRow ? 'bg-blueLight' : 'bg-transparent'
                      }`}
                      style={{ borderRadius: '12px' }}
                    >
                      <CustomCheckbox checked={wa.enabled} onChange={() => handleToggleEnabled(wa.id)} />
                      <span className="font-semibold text-blueDarker text-base">{wa.name}</span>
                    </div>
                  );
                })}
              {currentWalletProfileForm.walletsAttributes.filter((wa) => wa.walletType === 'pension').length === 0 && (
                <p className="text-sm text-gray-400 p-4 text-center">Nenhuma carteira de previdência disponível.</p>
              )}
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <CustomButton
              onClick={handleDiscard}
              fontSize="text-sm"
              bgColor="bg-blueUltraLight"
              borderColor="border-graySemiDark"
              className="text-grayDark"
            >
              DESCARTAR MUDANÇAS
            </CustomButton>
            <CustomButton onClick={handleSave} fontSize="text-sm" disabled={loading}>
              SALVAR ALTERAÇÕES
            </CustomButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletProfiles;
